import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import './PlasmaCuttingPage.css';

const SKU_ROWS = [
  ['BP-6x6-4H', 'Base Plate 6x6 4 Holes', '6x6', '1/4", 3/8", 1/2"', 'Includes perimeter + 4 holes'],
  ['BP-8x8-4H', 'Base Plate 8x8 4 Holes', '8x8', '1/4", 3/8", 1/2"', 'Includes perimeter + 4 holes'],
  ['BP-10x10-4H', 'Base Plate 10x10 4 Holes', '10x10', '1/4", 3/8", 1/2"', 'Includes perimeter + 4 holes'],
  ['BP-12x12-4H', 'Base Plate 12x12 4 Holes', '12x12', '1/4", 3/8", 1/2"', 'Includes perimeter + 4 holes'],
  ['G-4', 'Gusset 4 Inch', '4x4', '1/4", 3/8", 1/2"', 'Triangle'],
  ['G-6', 'Gusset 6 Inch', '6x6', '1/4", 3/8", 1/2"', 'Triangle'],
  ['G-8', 'Gusset 8 Inch', '8x8', '1/4", 3/8", 1/2"', 'Triangle'],
  ['G-12', 'Gusset 12 Inch', '12x12', '1/4", 3/8", 1/2"', 'Triangle'],
  ['EP-6x6', 'Embed Plate 6x6', '6x6', '1/4", 3/8", 1/2"', 'Perimeter only'],
  ['EP-8x8', 'Embed Plate 8x8', '8x8', '1/4", 3/8", 1/2"', 'Perimeter only'],
  ['EP-12x12', 'Embed Plate 12x12', '12x12', '1/4", 3/8", 1/2"', 'Perimeter only'],
  ['STR-2x48', 'Strip 2x48', '2x48', '1/4", 3/8", 1/2"', 'Perimeter only'],
  ['STR-4x96', 'Strip 4x96', '4x96', '1/4", 3/8", 1/2"', 'Perimeter only'],
];

const VALUE_POINTS = [
  'Fast quoting',
  'Consistent repeatable parts',
  'Common plate sizes ready to price',
  'Bulk quantities available',
];

const MATERIAL_DECK = [
  {
    id: 'stainless',
    title: 'Stainless',
    line: 'Plate and sheet for brackets, guards, embeds, and field fab where corrosion resistance matters.',
  },
  {
    id: 'aluminum',
    title: 'Aluminum',
    line: 'Plate and sheet when you need less weight on the truck and a clean cut for secondary machining.',
  },
  {
    id: 'mild-steel',
    title: 'Mild steel',
    line: 'Carbon steel plate for the majority of structural and misc. work—the standard SKUs below are built around this.',
  },
];

const PlasmaCuttingPage = () => {
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      offset: 100,
      easing: 'ease-out',
    });
    const prevTitle = document.title;
    document.title = 'Plasma Cutting | Advanced Metal Shop';
    return () => {
      document.title = prevTitle;
    };
  }, []);

  return (
    <div className="plasma-page">
      <div className="plasma-hero">
        <div className="plasma-hero-overlay" />
        <div className="plasma-hero-inner" data-aos="fade-up">
          <div className="plasma-hero-headline">
            <h1 className="gradient-text plasma-hero-title">Plasma Cutting</h1>
            <div className="section-underline-animated plasma-hero-underline" aria-hidden="true" />
          </div>
          <p className="plasma-hero-subtitle">Instant pricing. Fast turnaround. Consistent parts.</p>
        </div>
      </div>

      <section className="plasma-section plasma-intro">
        <div className="plasma-container">
          <p className="plasma-intro-copy" data-aos="fade-up">
            Advanced Metal Shop supplies standardized plasma-cut steel parts for fabricators, contractors,
            installers, and repeat buyers who need quick quoting without back-and-forth. Send your SKU and
            quantity; we price common plate sizes and ship production-ready parts on a schedule that keeps your
            crews moving.
          </p>
        </div>
      </section>

      <section className="plasma-section plasma-values">
        <div className="plasma-container">
          <h2 className="plasma-section-title" data-aos="fade-up">
            Why order standard parts from us
          </h2>
          <ul className="plasma-values-list">
            {VALUE_POINTS.map((text, index) => (
              <li key={text} className="plasma-value-item" data-aos="fade-up" data-aos-delay={100 * (index + 1)}>
                {text}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="plasma-section plasma-materials">
        <div className="plasma-container">
          <h2 className="plasma-section-title" data-aos="fade-up">
            Materials
          </h2>
          <p className="plasma-materials-lead" data-aos="fade-up">
            Plasma on plate and sheet—<strong>stainless</strong>, <strong>aluminum</strong>, and{' '}
            <strong>mild steel</strong>. Standard SKUs below are built for carbon steel; we quote the same
            geometries in other alloys when your project drawings and specifications call for it.
          </p>
          <div className="plasma-materials-deck">
            {MATERIAL_DECK.map((m, index) => (
              <article
                key={m.id}
                className={`plasma-material-tile plasma-material-tile--${m.id}`}
                data-aos="fade-up"
                data-aos-delay={120 * (index + 1)}
              >
                <h3 className="plasma-material-tile-title">{m.title}</h3>
                <p className="plasma-material-tile-line">{m.line}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="plasma-section plasma-table-section">
        <div className="plasma-container">
          <h2 className="plasma-section-title" data-aos="fade-up">
            Standard SKUs
          </h2>
          <p className="plasma-table-lead" data-aos="fade-up">
            Size column values are nominal outline dimensions in inches. Thickness options are in inches; cut scope is
            defined per SKU. For drawing file requirements, how nominal catalog parts may vary, and how to request a
            fixed configuration, read{' '}
            <a className="plasma-inline-anchor" href="#order-requirements">
              Order requirements
            </a>{' '}
            at the end of this page.
          </p>
          <div
            className="plasma-table-scroll"
            tabIndex={0}
            role="region"
            aria-label="Standard plasma cut parts pricing table"
            data-aos="fade-up"
          >
            <table className="plasma-table">
              <thead>
                <tr>
                  <th scope="col">SKU</th>
                  <th scope="col">Description</th>
                  <th scope="col">Size (in.)</th>
                  <th scope="col">Thickness options (in.)</th>
                  <th scope="col">Notes</th>
                </tr>
              </thead>
              <tbody>
                {SKU_ROWS.map(([sku, description, size, thickness, notes]) => (
                  <tr key={sku}>
                    <th scope="row">{sku}</th>
                    <td>{description}</td>
                    <td>{size}</td>
                    <td>{thickness}</td>
                    <td>{notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="plasma-section plasma-custom-cuts" data-aos="fade-up">
        <div className="plasma-container">
          <h2 className="plasma-section-title">Custom cuts</h2>
          <div className="plasma-custom-cuts-card">
            <p>
              Anything outside the standard SKU list is a custom cut and <strong>requires consultation</strong> so we
              can confirm geometry, material, tolerances, and schedule before we quote. The same cut-ready{' '}
              <strong>DXF</strong> or <strong>DWG</strong> file requirement applies. Add detail in writing when
              dimensions, hole patterns, or fit differ from a straightforward plate outline, and we will work through
              options with you.
            </p>
          </div>
        </div>
      </section>

      <section
        className="plasma-section plasma-order-requirements"
        data-aos="fade-up"
        id="order-requirements"
        aria-labelledby="plasma-order-requirements-heading"
      >
        <div className="plasma-container">
          <h2 className="plasma-section-title" id="plasma-order-requirements-heading">
            Order requirements
          </h2>
          <div className="plasma-requirements-card">
            <p>
              <strong>Drawing files.</strong> All plasma-cut orders—standard SKU or custom—require cut-ready{' '}
              <strong>DXF</strong> or <strong>DWG</strong> files that define the geometry to be nested, programmed, and
              cut. We use your drawings for quoting, CAM, and production verification. Include a short written scope
              or tolerance note when the file alone does not describe fit, finish, or field intent.
            </p>
            <p>
              <strong>Standard catalog SKUs (nominal).</strong> The standard list <strong>above</strong> describes
              nominal dimensions and typical hole patterns. Unless you specify a locked configuration in your order and
              we confirm it in writing, finished parts may reflect ordinary fabrication variation: for example, a
              greater or smaller number of holes, small shifts in hole center locations, or minor size differences,
              provided they remain within typical shop practice and do not change the part&apos;s fit or function for
              the stated use.
            </p>
          </div>
        </div>
      </section>

      <section
        className="plasma-contact-band"
        data-aos="fade-up"
        aria-labelledby="plasma-contact-heading"
      >
        <div className="plasma-container plasma-contact-band-inner">
          <h2 id="plasma-contact-heading" className="plasma-parts-cta-title gradient-text">
            Plasma Cutting
          </h2>
          <div className="section-underline-narrow plasma-quote-cta-underline" data-aos="zoom-in" aria-hidden="true" />
          <p className="plasma-quote-cta-lead">Need a fast quote?</p>
          <p className="plasma-quote-cta-text">
            Send us your SKU and quantity and we&apos;ll get pricing turned around quickly.
          </p>
          <Link to="/contact" className="plasma-quote-btn">
            Contact us
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PlasmaCuttingPage;
